import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Home, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  ChevronRight,
  DollarSign,
  Wallet,
  ArrowLeft
} from 'lucide-react';
import useBillsStore from '../../../store/billsStore';
import useWalletStore from '../../../store/walletStore';
import { toast } from 'react-hot-toast';

const ElectricityTab = () => {
  const [step, setStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [meterNumber, setMeterNumber] = useState('');
  const [meterType, setMeterType] = useState('prepaid');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  
  const { 
    electricityProviders, 
    recentMeters, 
    validateMeter, 
    purchaseElectricity, 
    isLoading,
    getElectricityProviders 
  } = useBillsStore();
  
  const { wallet } = useWalletStore();

  useEffect(() => {
    getElectricityProviders();
  }, []);

  const presetAmounts = [500, 1000, 2000, 5000, 10000];

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setStep(2);
  };

  const handleValidateMeter = async () => {
    if (!meterNumber || meterNumber.length < 6) {
      toast.error('Please enter a valid meter number');
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateMeter({
        serviceId: selectedProvider.id,
        meterNumber,
        meterType,
      });

      setValidationResult(result);
      
      if (result.success && result.data.verified) {
        setStep(3);
        toast.success(`Meter validated! Customer: ${result.data.customerName}`);
      }
    } catch (error) {
      console.error('Meter validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handlePresetSelect = (preset) => {
    setAmount(preset.toString());
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    const cleaned = value.replace(/\D/g, '');
    setCustomAmount(cleaned);
    setAmount('');
  };

  const handlePurchase = async () => {
    const purchaseAmount = amount || customAmount;
    const numericAmount = parseFloat(purchaseAmount);

    if (!numericAmount || numericAmount < 100) {
      toast.error('Minimum electricity purchase is ₦100');
      return;
    }

    if (numericAmount > 100000) {
      toast.error('Maximum electricity purchase is ₦100,000');
      return;
    }

    if (wallet?.balance < numericAmount) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      const result = await purchaseElectricity({
        serviceId: selectedProvider.id,
        meterNumber,
        amount: numericAmount,
        meterType,
      });

      setPurchaseResult(result);
      
      if (result.success) {
        setStep(4);
      }
    } catch (error) {
      console.error('Electricity purchase failed:', error);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatToken = (token) => {
    if (!token) return '';
    return token.match(/.{1,4}/g)?.join('-') || token;
  };

  const renderStep1 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h3 className="text-sm font-semibold text-gray-800">Select Electricity Provider</h3>
        </div>
        <p className="text-xs text-gray-500">Choose your electricity distribution company</p>
      </div>

      {/* Recent Meters */}
      {recentMeters.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-600 mb-2">Recent Meters</h4>
          <div className="flex flex-wrap gap-2">
            {recentMeters.map((meter) => (
              <button
                key={meter}
                onClick={() => setMeterNumber(meter)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#E8ECF5] shadow-neomorph-inset hover:shadow-neomorph transition-all text-xs font-medium text-gray-700"
              >
                <Home className="h-3.5 w-3.5 mr-1.5" />
                <span className="truncate max-w-[100px]">{meter}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Providers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {electricityProviders.map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleProviderSelect(provider)}
            className="p-3 sm:p-4 rounded-xl bg-white shadow-neomorph hover:shadow-neomorph-hover border border-gray-100 text-left transition-all"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-md flex-shrink-0">
                <Zap className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-gray-800 truncate">{provider.name}</h4>
                <p className="text-xs text-gray-500 truncate">{provider.code}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center mb-1">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Enter Meter Details</h3>
          </div>
          <p className="text-xs text-gray-500">Provider: {selectedProvider?.name}</p>
        </div>
        <button
          onClick={() => {
            setStep(1);
            setSelectedProvider(null);
            setValidationResult(null);
          }}
          className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset w-full sm:w-auto"
        >
          Change Provider
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="meterNumber" className="block text-xs font-medium text-gray-600 mb-1.5">
            Meter Number
          </label>
          <input
            id="meterNumber"
            type="text"
            value={meterNumber}
            onChange={(e) => setMeterNumber(e.target.value.replace(/\D/g, ''))}
            className="block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            placeholder="Enter your meter number"
            maxLength={20}
          />
          <p className="mt-1.5 text-xs text-gray-500">Enter your 11-20 digit meter number</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Meter Type
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="meterType"
                value="prepaid"
                checked={meterType === 'prepaid'}
                onChange={(e) => setMeterType(e.target.value)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-xs text-gray-700">Prepaid</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="meterType"
                value="postpaid"
                checked={meterType === 'postpaid'}
                onChange={(e) => setMeterType(e.target.value)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <span className="ml-2 text-xs text-gray-700">Postpaid</span>
            </label>
          </div>
        </div>

        {validationResult && (
          <div className={`rounded-xl p-4 ${
            validationResult.success && validationResult.data.verified
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          } shadow-neomorph-inset`}>
            <div className="flex items-start">
              <div className={`p-1.5 rounded-md ${
                validationResult.success && validationResult.data.verified
                  ? 'bg-green-100'
                  : 'bg-red-100'
              } shadow-neomorph-inset mr-3 flex-shrink-0`}>
                {validationResult.success && validationResult.data.verified ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className={`text-xs font-medium ${
                  validationResult.success && validationResult.data.verified
                    ? 'text-green-800'
                    : 'text-red-800'
                } mb-1`}>
                  {validationResult.success && validationResult.data.verified
                    ? 'Meter Verified'
                    : 'Verification Failed'}
                </h4>
                <p className={`text-xs ${
                  validationResult.success && validationResult.data.verified
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {validationResult.message}
                </p>
                {validationResult.success && validationResult.data.verified && (
                  <p className="text-xs font-medium text-green-800 mt-2 bg-white rounded-lg px-2 py-1 shadow-neomorph-inset break-words">
                    Customer: {validationResult.data.customerName}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => setStep(1)}
            className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
          >
            Back
          </button>
          <button
            onClick={handleValidateMeter}
            disabled={isValidating || !meterNumber || meterNumber.length < 6}
            className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isValidating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              'Validate Meter'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center mb-1">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Select Amount</h3>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {selectedProvider?.name} - {meterNumber}
          </p>
          {validationResult?.data?.customerName && (
            <p className="text-xs text-gray-400 truncate">{validationResult.data.customerName}</p>
          )}
        </div>
        <button
          onClick={() => setStep(2)}
          className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset flex items-center justify-center w-full sm:w-auto"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Back
        </button>
      </div>

      <div className="space-y-4 sm:space-y-5">
        {/* Quick Select Presets */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Quick Select
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {presetAmounts.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePresetSelect(preset)}
                className={`
                  py-2.5 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all border
                  ${amount === preset.toString()
                    ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-neomorph-inset'
                    : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                  }
                `}
              >
                ₦{preset.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div>
          <label htmlFor="customAmount" className="block text-xs font-medium text-gray-600 mb-1.5">
            Or Enter Custom Amount
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">₦</span>
            </div>
            <input
              type="text"
              id="customAmount"
              value={customAmount}
              onChange={(e) => handleCustomAmount(e.target.value)}
              className="block w-full pl-8 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="bg-blue-50 rounded-xl p-4 shadow-neomorph-inset border border-blue-200/50">
          <div className="flex items-start">
            <div className="p-1.5 bg-blue-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
              <Wallet className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-medium text-blue-800 mb-2">Wallet Balance</h4>
              <div className="space-y-1">
                <div className="flex justify-between text-xs flex-wrap gap-1">
                  <span className="text-blue-700">Available Balance:</span>
                  <span className="font-semibold text-blue-800">{formatAmount(wallet?.balance)}</span>
                </div>
                {(amount || customAmount) && (
                  <div className="flex justify-between text-xs flex-wrap gap-1">
                    <span className="text-blue-700">After Purchase:</span>
                    <span className={`font-semibold ${
                      wallet?.balance >= parseFloat(amount || customAmount)
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {formatAmount(wallet?.balance - parseFloat(amount || customAmount))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => setStep(2)}
            className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
          >
            Back
          </button>
          <button
            onClick={handlePurchase}
            disabled={isLoading || (!amount && !customAmount)}
            className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Purchase Electricity'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
          <CheckCircle className="h-8 w-8 text-white" />
        </div>
        <div className="flex items-center justify-center mb-1">
          <div className="w-1 h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
          <h3 className="text-sm font-semibold text-gray-800">Purchase Successful!</h3>
        </div>
        <p className="text-xs text-gray-500">Your electricity purchase was completed successfully</p>
      </div>

      {purchaseResult?.data?.token && (
        <div className="bg-yellow-50 rounded-xl p-4 shadow-neomorph-inset border border-yellow-200/50">
          <h4 className="text-xs font-medium text-yellow-800 mb-3">Electricity Token</h4>
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-mono font-bold text-gray-800 bg-white rounded-lg p-3 shadow-neomorph-inset mb-3 break-all">
              {formatToken(purchaseResult.data.token)}
            </div>
            <p className="text-xs text-yellow-700">
              Enter this token into your prepaid meter to recharge your electricity
            </p>
          </div>
        </div>
      )}

      <div className="bg-[#E8ECF5] rounded-xl p-4 shadow-neomorph-inset">
        <h4 className="text-xs font-medium text-gray-600 mb-3">Transaction Details</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-xs flex-wrap gap-1">
            <span className="text-gray-500">Provider</span>
            <span className="font-medium text-gray-800">{selectedProvider?.name}</span>
          </div>
          <div className="flex justify-between text-xs flex-wrap gap-1">
            <span className="text-gray-500">Meter Number</span>
            <span className="font-medium text-gray-800">{meterNumber}</span>
          </div>
          <div className="flex justify-between text-xs flex-wrap gap-1">
            <span className="text-gray-500">Amount</span>
            <span className="font-semibold text-primary-600">
              {formatAmount(parseFloat(amount || customAmount))}
            </span>
          </div>
          <div className="flex justify-between text-xs flex-wrap gap-1">
            <span className="text-gray-500">Transaction ID</span>
            <span className="text-xs font-mono text-gray-600 bg-white rounded-lg px-2 py-1 shadow-neomorph-inset break-all">
              {purchaseResult?.data?.transaction?.id?.slice(0, 8)}...
            </span>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 rounded-xl p-4 shadow-neomorph-inset border border-yellow-200/50">
        <div className="flex items-start">
          <div className="p-1.5 bg-yellow-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-xs font-medium text-yellow-800 mb-2">Important Information</h4>
            <ul className="text-xs text-yellow-700 space-y-1 list-disc pl-4 break-words">
              <li>The token has been sent to your registered phone number</li>
              <li>Enter the token into your prepaid meter within 24 hours</li>
              <li>Keep this receipt for your records</li>
              <li>Contact support if you encounter any issues</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={() => {
            setStep(1);
            setSelectedProvider(null);
            setMeterNumber('');
            setAmount('');
            setCustomAmount('');
            setValidationResult(null);
            setPurchaseResult(null);
          }}
          className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
        >
          New Purchase
        </button>
        <button
          onClick={() => window.location.href = '/bills'}
          className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white"
        >
          View All Bills
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Progress Steps */}
      <div className="flex items-center justify-center overflow-x-auto px-2">
        <div className="flex items-center min-w-max">
          {[1, 2, 3, 4].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold ${
                step >= stepNum 
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {stepNum}
              </div>
              {stepNum < 4 && (
                <div className={`w-6 sm:w-8 h-1 mx-1 rounded-full ${
                  step > stepNum ? 'bg-gradient-to-r from-primary-500 to-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-5 shadow-neomorph border border-gray-100">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ElectricityTab;